import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { getPublicShopBranding } from '@/api/shop';
import { printTrifoldCard, TRIFOLD_POPUP_FEATURES } from '@/utils/printTrifoldCard';
import { printRunSheet, RUN_SHEET_POPUP_FEATURES } from '@/utils/printRunSheet';
import { orderForOperationalUse, printUsesConfirmedValues } from '@/utils/offlineEditContinuity';

export const PRINT_BRANDING_WAIT_MS = 10000;
const fields = ['id', 'internal_order_number', 'order_number', 'card_message', 'recipient_name',
  'recipient_street_address', 'recipient_street_address_2', 'recipient_city',
  'recipient_state', 'recipient_zip_code', 'recipient_phone', 'resolved_source', 'source_resolution_status'];
const capture = (value, keys) => value == null ? null : Object.fromEntries(keys.map(key => {
  const item = value[key];
  return [key, item == null || ['string', 'number', 'boolean'].includes(typeof item) ? item : String(item)];
}));
export const captureTrifoldOrder = order => {
  const snapshot = capture(orderForOperationalUse(order), fields);
  if (snapshot && order.desktop_sync) snapshot.desktop_sync = capture(order.desktop_sync, ['origin']);
  return snapshot;
};
const captureRun = run => ({
  ...capture(run, ['id', 'name', 'date', 'origin', 'is_offline_draft', 'user_id']),
  driver: capture(run.driver, ['name']),
  populated_stops: (run.populated_stops || []).map(stop => ({ order: captureTrifoldOrder(stop.order || {}) })),
});
const recordKey = record => record?.id == null ? null
  : JSON.stringify([record.id, record.origin || record.desktop_sync?.origin || null, !!record.is_offline_draft]);

// Own only pending preparation/automatic printing. Published documents remain
// independent snapshots; no caller fetches a fresh business record to print.
export default function usePreparedPrint({ kind = 'trifold', record, records, shop,
  fetchBranding = kind === 'trifold', canPrint = true }) {
  const session = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const latest = useRef(null);
  latest.current = { kind, record, records, shop, fetchBranding, canPrint, session };
  const mounted = useRef(false);
  const operations = useRef(new Map());

  useLayoutEffect(() => {
    mounted.current = true;
    const owned = operations.current;
    return () => {
      mounted.current = false;
      [...owned.values()].forEach(operation => operation.cancel());
    };
  }, [session, kind]);
  useLayoutEffect(() => {
    [...operations.current.values()].forEach(operation => {
      if (!operation.current()) operation.cancel();
    });
  });

  return useCallback((recordOverride, options = {}) => {
    const input = latest.current;
    const source = recordOverride || input.record;
    const key = recordKey(source);
    const contains = state => (state.records || [state.record]).some(item => recordKey(item) === key);
    if (!mounted.current || !key || !input.canPrint || !contains(input)
      || input.session !== getActiveSessionRevision()) return;
    const previous = operations.current.get(key);
    if (previous?.current()) {
      try { previous.popup.focus(); } catch { /* Never create a replacement from focus failure. */ }
      return;
    }
    previous?.cancel();

    let snapshot; let branding; let popup;
    try {
      snapshot = input.kind === 'run' ? captureRun(source) : captureTrifoldOrder(source);
      branding = capture(input.shop, ['name', 'address', 'logo_id']);
      popup = window.open('', '_blank', input.kind === 'run' ? RUN_SHEET_POPUP_FEATURES : TRIFOLD_POPUP_FEATURES);
      if (!popup) { toast.error('Pop-ups blocked — allow pop-ups to print'); return; }
      popup.opener = null;
      if (input.kind !== 'run' && printUsesConfirmedValues(source)) {
        toast.info('Printing confirmed values. Pending offline changes still need review.');
      }
    } catch {
      try { popup?.close(); } catch { /* Dispose only our unfinished popup. */ }
      toast.error('Failed to prepare print document');
      return;
    }
    const controller = new AbortController();
    const operation = { popup, published: false, cancelled: false, settled: false, cancelAutoPrint: null };
    let timer;
    const current = () => mounted.current && !operation.cancelled && !operation.settled
      && operations.current.get(key) === operation && !popup.closed && latest.current.canPrint
      && latest.current.kind === input.kind && latest.current.session === input.session
      && getActiveSessionRevision() === input.session && contains(latest.current);
    operation.current = current;
    const releasePreparation = () => {
      clearTimeout(timer);
      controller.abort();
      popup.removeEventListener?.('pagehide', operation.cancel);
    };
    const settled = () => {
      if (operation.settled) return;
      operation.settled = true;
      releasePreparation();
      if (operations.current.get(key) === operation) operations.current.delete(key);
    };
    operation.cancel = () => {
      if (operation.cancelled || operation.settled) return;
      operation.cancelled = true;
      operation.cancelAutoPrint?.();
      settled();
      if (!operation.published) {
        try { if (!popup.closed) popup.close(); } catch { /* No replacement/retry. */ }
      }
    };
    operations.current.set(key, operation);
    const capturedOptions = { includeOrderNumber: options.includeOrderNumber !== false };
    const publish = (value, brandingFallback = false) => {
      if (!current()) { operation.cancel(); return; }
      releasePreparation(); // document.open retires the placeholder document.
      try {
        const lifecycle = { popup, isCurrent: current, onSettled: settled };
        const result = input.kind === 'run'
          ? printRunSheet(snapshot, lifecycle)
          : printTrifoldCard(snapshot, capture(value, ['name', 'address', 'logo_id']),
            { ...capturedOptions, ...lifecycle, brandingFallback });
        operation.published = true;
        operation.cancelAutoPrint = result?.cancelAutoPrint;
        if (!operation.settled && !operation.cancelled) popup.addEventListener?.('pagehide', operation.cancel);
      } catch {
        const report = current();
        operation.cancel();
        if (report) toast.error('Failed to prepare print document');
      }
    };
    if (input.kind === 'run' || !input.fetchBranding) { publish(branding); return; }
    try {
      popup.document.open();
      popup.document.write('<!doctype html><html><head><title>Preparing enclosure card</title></head><body>Preparing enclosure card…</body></html>');
      popup.document.close();
      popup.addEventListener?.('pagehide', operation.cancel);
    } catch {
      operation.cancel();
      toast.error('Failed to prepare print document');
      return;
    }
    // A missed close event is still caught on settlement/deadline; no close polling.
    timer = setTimeout(() => publish(null, true), PRINT_BRANDING_WAIT_MS);
    try {
      Promise.resolve(getPublicShopBranding({ signal: controller.signal })).then(
        value => { if (!operation.settled && !operation.cancelled && !operation.published) publish(value); },
        () => { if (!operation.settled && !operation.cancelled && !operation.published) publish(null, true); },
      );
    } catch { publish(null, true); }
  }, []);
}
